import { useState, useMemo } from 'react'
import type { SortDirection } from '@/shared/components/sortable-th'

export function useTableSort<T>(data: T[] | undefined, defaultKey: string) {
  const [sortKey, setSortKey] = useState<string>(defaultKey)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const sortedData = useMemo(() => {
    if (!data) return []
    if (!sortKey || !sortDirection) return data

    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortKey]
      const bVal = (b as Record<string, unknown>)[sortKey]

      let cmp = 0
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal)
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal
      } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        cmp = aVal === bVal ? 0 : aVal ? -1 : 1
      } else {
        cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''))
      }

      return sortDirection === 'desc' ? -cmp : cmp
    })
  }, [data, sortKey, sortDirection])

  return { sortedData, sortKey, sortDirection, handleSort }
}
